import React, { useEffect, useState } from "react";
import {
  X,
  Calendar,
  Loader2,
  Paperclip,
  AlertTriangle,
  Sun,
  FileText,
} from "lucide-react";
import Select from "react-select";
import {
  createOnLeave,
  getRemainingAnnualLeave,
} from "../../../api/hrm/on_leaves/onLeave";
import {
  showSuccessToast,
  showErrorToast,
} from "../../../components/ConfirmDialog";
import { AnimatePresence, motion } from "framer-motion";
import customSelectStyles from "../../../components/ui/customSelectStyles";

const labelClass =
  "text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1";
const inputClass =
  "w-full bg-white border border-gray-300 rounded-2xl px-4 py-3 text-sm font-medium text-gray-800 outline-none transition-all hover:border-gray-400 focus:bg-white focus:border-red-500 focus:ring-4 focus:ring-red-500/5";

const selectStyles = {
  ...customSelectStyles,
  control: (provided, state) => ({
    ...(typeof customSelectStyles.control === "function"
      ? customSelectStyles.control(provided, state)
      : provided),
    minHeight: 48,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: state.isFocused ? "#ef4444" : "#d1d5db",
    backgroundColor: "#fff",
    boxShadow: state.isFocused ? "0 0 0 4px rgba(239, 68, 68, 0.05)" : "none",
    "&:hover": {
      borderColor: state.isFocused ? "#ef4444" : "#9ca3af",
    },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 999999 }),
};

const typeOptions = [
  { value: "Phép thường", label: "Phép thường (nghỉ không lương)" },
  { value: "Phép năm", label: "Phép năm (nghỉ có lương)" },
  { value: "Bảo hiểm xã hội", label: "Bảo hiểm xã hội" },
];

const sessionOptions = [
  { value: "full", label: "Cả ngày" },
  { value: "morning", label: "Buổi sáng" },
  { value: "afternoon", label: "Buổi chiều" },
];

const OnLeaveCreateModal = ({ isOpen, onClose, onSaved }) => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [startSession, setStartSession] = useState("full");
  const [endSession, setEndSession] = useState("full");
  const [reason, setReason] = useState("");
  const [remainingAnnualLeave, setRemainingAnnualLeave] = useState(null);
  const [attachedFile, setAttachedFile] = useState(null);
  const [dateError, setDateError] = useState("");
  const [totalDays, setTotalDays] = useState(0);

  useEffect(() => {
    if (isOpen) {
      const today = new Date().toISOString().split("T")[0];
      setFromDate(today);
      setToDate(today);
      setSelectedType(null);
      setReason("");
      setStartSession("full");
      setEndSession("full");
      setAttachedFile(null);
      setTotalDays(0);
      setDateError("");
      setRemainingAnnualLeave(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!fromDate || !toDate) return;

    const d1 = new Date(fromDate);
    const d2 = new Date(toDate);

    if (d2 < d1) {
      setDateError("Ngày đến không được nhỏ hơn ngày bắt đầu");
      setTotalDays(0);
      return;
    }

    setDateError("");

    const diff = (d2 - d1) / (1000 * 60 * 60 * 24) + 1;

    if (diff === 1) {
      setTotalDays(startSession === "full" ? 1 : 0.5);
      return;
    }

    const middleDays = diff - 2;
    let total = 0;
    total += startSession === "full" ? 1 : 0.5;
    total += endSession === "full" ? 1 : 0.5;
    if (middleDays > 0) total += middleDays;
    setTotalDays(total);
  }, [fromDate, toDate, startSession, endSession]);

  useEffect(() => {
    const loadRemaining = async () => {
      if (selectedType?.value === "Phép năm" && user?.id) {
        try {
          const res = await getRemainingAnnualLeave(user.id);
          setRemainingAnnualLeave(res.remaining_days ?? 0);
        } catch (err) {
          console.error(err);
          showErrorToast("Không lấy được số phép năm còn lại");
        }
      } else {
        setRemainingAnnualLeave(null);
      }
    };
    loadRemaining();
  }, [selectedType, user?.id]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 25 * 1024 * 1024;
    if (file.type !== "application/pdf") {
      showErrorToast("Chỉ chấp nhận file PDF.");
      e.target.value = null;
      return;
    }
    if (file.size > maxSize) {
      showErrorToast("Dung lượng tối đa là 25MB.");
      e.target.value = null;
      return;
    }
    setAttachedFile(file);
  };

  const handleSave = async () => {
    if (!selectedType) return showErrorToast("Vui lòng chọn loại phép");
    if (!fromDate || !toDate) return showErrorToast("Vui lòng chọn ngày");
    if (dateError) return showErrorToast(dateError);
    if (!reason.trim()) return showErrorToast("Vui lòng nhập lý do");

    if (selectedType.value === "Bảo hiểm xã hội" && !attachedFile) {
      return showErrorToast("Vui lòng đính kèm chứng từ BHXH");
    }

    if (
      selectedType.value === "Phép năm" &&
      remainingAnnualLeave !== null &&
      totalDays > remainingAnnualLeave
    ) {
      return showErrorToast(
        `Số ngày đăng ký (${totalDays}) vượt số phép còn lại (${remainingAnnualLeave})`
      );
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("employee_id", user.id);
      formData.append("leave_type", selectedType.value);
      formData.append("from_date", fromDate);
      formData.append("to_date", toDate);
      formData.append("start_session", startSession);
      formData.append("end_session", endSession);
      formData.append("reason", reason.trim());

      if (attachedFile) {
        formData.append("attached_file", attachedFile);
      }

      await createOnLeave(formData);

      showSuccessToast("Đăng ký nghỉ phép thành công!");
      onSaved?.();
      onClose?.();
    } catch (e) {
      console.error(e);
      showErrorToast("Không thể tạo đơn nghỉ phép");
    } finally {
      setLoading(false);
    }
  };

  const sameDay = fromDate && toDate && fromDate === toDate;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[200] bg-black/45 backdrop-blur-sm"
            onClick={onClose}
          />

          <div className="fixed inset-0 z-[210] flex items-end justify-center p-0 pointer-events-none sm:items-center sm:p-4">
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 320 }}
              className="pointer-events-auto flex h-[90dvh] w-full max-w-3xl flex-col overflow-hidden rounded-t-[1.75rem] border border-gray-200 bg-white shadow-2xl sm:h-auto sm:max-h-[90vh] sm:rounded-[2rem]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 bg-gradient-to-r from-red-50/70 via-white to-white px-5 py-4 sm:px-6">
                <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                  <div className="rounded-2xl border border-red-700/10 bg-red-700/5 p-3 text-red-700 shadow-sm">
                    <Sun className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.25} />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg font-black uppercase tracking-tight text-gray-800 sm:text-xl">
                      Đăng ký <span className="text-red-700">nghỉ phép</span>
                    </h2>
                    <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400">
                      Chọn loại phép, thời gian và lý do nghỉ
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl border border-gray-200 bg-white p-2 text-gray-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                  aria-label="Đóng"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Body */}
              <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain px-5 py-5 sm:px-6">
                {/* Loại phép */}
                <section className="space-y-4 rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-50/80 to-white p-4 sm:p-5">
                  <h3 className="text-[11px] font-black uppercase tracking-widest text-gray-500">
                    Loại phép
                  </h3>
                  <div>
                    <label className={`${labelClass} mb-1.5 block`}>
                      Chọn loại <span className="text-red-500">*</span>
                    </label>
                    <Select
                      styles={selectStyles}
                      value={selectedType}
                      options={typeOptions}
                      onChange={setSelectedType}
                      placeholder="Chọn loại phép..."
                      menuPortalTarget={document.body}
                    />
                  </div>

                  {selectedType?.value === "Phép năm" ? (
                    <div className="flex items-center gap-3 rounded-2xl border border-amber-100 bg-amber-50/80 px-4 py-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                        <Calendar size={16} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[11px] font-bold uppercase tracking-wider text-amber-800/80">
                          Phép năm còn lại
                        </p>
                        <p className="text-sm font-black text-gray-900">
                          {remainingAnnualLeave !== null
                            ? `${remainingAnnualLeave} ngày`
                            : "Đang tải..."}
                        </p>
                      </div>
                    </div>
                  ) : null}
                </section>

                {/* Thời gian */}
                <section className="space-y-4 rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-50/80 to-white p-4 sm:p-5">
                  <h3 className="text-[11px] font-black uppercase tracking-widest text-gray-500">
                    Thời gian nghỉ
                  </h3>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label
                        className={`${labelClass} mb-1.5 flex items-center gap-1.5`}
                      >
                        <Calendar size={12} className="text-red-500" />
                        Từ ngày <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label
                        className={`${labelClass} mb-1.5 flex items-center gap-1.5`}
                      >
                        <Calendar size={12} className="text-red-500" />
                        Đến ngày <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        className={inputClass}
                      />
                    </div>
                  </div>

                  {fromDate && toDate ? (
                    <div
                      className={`grid gap-4 ${
                        sameDay ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"
                      }`}
                    >
                      <div>
                        <label className={`${labelClass} mb-1.5 block`}>
                          {sameDay ? "Buổi nghỉ" : "Buổi ngày bắt đầu"}
                        </label>
                        <Select
                          styles={selectStyles}
                          value={sessionOptions.find(
                            (o) => o.value === startSession
                          )}
                          onChange={(option) => setStartSession(option.value)}
                          options={sessionOptions}
                          placeholder="Chọn buổi"
                          menuPortalTarget={document.body}
                        />
                      </div>
                      {!sameDay ? (
                        <div>
                          <label className={`${labelClass} mb-1.5 block`}>
                            Buổi ngày kết thúc
                          </label>
                          <Select
                            styles={selectStyles}
                            value={sessionOptions.find(
                              (o) => o.value === endSession
                            )}
                            onChange={(option) => setEndSession(option.value)}
                            options={sessionOptions}
                            placeholder="Chọn buổi"
                            menuPortalTarget={document.body}
                          />
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {dateError ? (
                    <div className="flex items-start gap-2 rounded-2xl border border-rose-100 bg-rose-50 px-3.5 py-3 text-sm text-rose-700">
                      <AlertTriangle
                        size={16}
                        className="mt-0.5 shrink-0"
                      />
                      <span className="font-medium">{dateError}</span>
                    </div>
                  ) : null}

                  {totalDays > 0 && !dateError ? (
                    <div className="inline-flex items-center gap-2 rounded-full border border-sky-100 bg-sky-50 px-3.5 py-1.5">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-sky-700/80">
                        Tổng số ngày nghỉ
                      </span>
                      <span className="text-sm font-black tabular-nums text-sky-800">
                        {totalDays}
                      </span>
                    </div>
                  ) : null}
                </section>

                {/* Lý do + file */}
                <section className="space-y-4 rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-50/80 to-white p-4 sm:p-5">
                  <h3 className="text-[11px] font-black uppercase tracking-widest text-gray-500">
                    Lý do & chứng từ
                  </h3>

                  <div>
                    <label className={`${labelClass} mb-1.5 block`}>
                      Lý do <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      rows={3}
                      placeholder="Nhập lý do nghỉ phép..."
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      maxLength={250}
                      className={`${inputClass} resize-none`}
                    />
                    <p
                      className={`mt-1 text-right text-[11px] ${
                        reason.length > 200
                          ? "font-bold text-red-500"
                          : "text-gray-400"
                      }`}
                    >
                      {reason.length}/250
                    </p>
                  </div>

                  {selectedType?.value === "Bảo hiểm xã hội" ? (
                    <div>
                      <label className={`${labelClass} mb-1.5 flex items-center gap-1.5`}>
                        <Paperclip size={12} className="text-red-500" />
                        File đính kèm <span className="text-red-500">*</span>
                      </label>
                      <label className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-300 bg-white px-4 py-8 text-center transition hover:border-red-300 hover:bg-red-50/30">
                        <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-red-50 text-red-700">
                          <FileText size={18} />
                        </div>
                        <span className="text-sm font-bold text-red-700">
                          Chọn tệp PDF để tải lên
                        </span>
                        <span className="mt-1 text-[11px] text-gray-400">
                          Kéo thả hoặc nhấn để chọn · tối đa 25 MB
                        </span>
                        <input
                          type="file"
                          accept="application/pdf"
                          className="hidden"
                          onChange={handleFileChange}
                        />
                      </label>
                      {attachedFile ? (
                        <div className="mt-2 flex items-center gap-2 rounded-xl border border-emerald-100 bg-emerald-50/70 px-3 py-2 text-sm text-emerald-800">
                          <Paperclip size={14} className="shrink-0" />
                          <span className="min-w-0 truncate font-medium">
                            {attachedFile.name}
                          </span>
                          <span className="shrink-0 text-[11px] text-emerald-700/70">
                            {(attachedFile.size / 1024 / 1024).toFixed(2)} MB
                          </span>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </section>
              </div>

              {/* Footer */}
              <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50/80 px-5 py-4 sm:flex-row sm:justify-end sm:gap-3 sm:px-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-full border border-gray-200 bg-white px-6 py-2.5 text-xs font-bold uppercase tracking-wider text-gray-600 transition hover:border-gray-300 hover:bg-gray-50"
                >
                  Hủy
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-red-700 to-red-800 px-6 py-2.5 text-xs font-black uppercase tracking-wider text-white shadow-md shadow-red-100 transition hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Đang lưu...
                    </>
                  ) : (
                    "Gửi đăng ký"
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default OnLeaveCreateModal;
